import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Check, AlertTriangle, RotateCcw, Mic, Send, ExternalLink, X,
  Activity, Stethoscope, Sparkles, FileText, Video, Copy, Zap, FileWarning,
  Pause, Archive, MoreVertical, Square, CheckSquare, PlayCircle,
  PenLine, FileCheck2, ChevronRight, ChevronDown,
} from "lucide-react";
import {
  getVisit, transcribeVisit, sendVisitToClaim, PIPELINE_STAGES, pipelineState,
  getAppointmentDocuments, getAppointmentDocument, processBatch, setSessionState, SESSION_STATES, isFrozen,
} from "@/lib/telehealth";
import { getNote, signNote, generateClaim } from "@/lib/noteClaim";
import { isSigned, isGenerated } from "@/lib/createClaimsModel";
import { useChartTabs } from "@/lib/chartTabs";
import { patientOf } from "@/lib/claimReadiness";

const IND = "#4B4BE8";
const fmtDT = (iso) => { const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };
const STAGE_ICON = { recording: Video, transcript: Mic, insights: Sparkles, soap: Stethoscope, claim: FileText };

function copyText(text, onOk) {
  const done = () => { onOk && onOk(); };
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p && p.then) { p.then(done).catch(() => fallbackCopy(text, done)); return; }
  } catch { /* fall through */ }
  fallbackCopy(text, done);
}
function fallbackCopy(text, done) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    done && done();
  } catch { /* ignore — clipboard unavailable */ }
}

function VisitIdChip({ visitId }) {
  const [copied, setCopied] = useState(false);
  if (!visitId) return null;
  const copy = (e) => { e.stopPropagation(); copyText(visitId, () => { setCopied(true); setTimeout(() => setCopied(false), 1200); }); };
  return (
    <button onClick={copy} data-testid={`visit-id-chip-${visitId}`} title="Copy visit ID"
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-[6px] font-mono" style={{ background: "#F1F1F0", color: "#6C6C69" }}>
      {copied ? <Check size={10} /> : <Copy size={10} />} {String(visitId).slice(0, 8)}
    </button>
  );
}

function SessionBadge({ state }) {
  const meta = SESSION_STATES[state];
  if (!meta || state === "active") return null;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }} data-testid={`session-badge-${state}`}>{meta.label}</span>;
}

// Per-row session-state menu: active / stale / removed (§6.10).
function StateControl({ visitId, state, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const opts = [["active", "Mark active", PlayCircle], ["stale", "Mark stale", Pause], ["removed", "Mark removed", Archive]];
  const cur = state || "active";
  return (
    <span ref={ref} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`state-menu-${visitId}`} title="Session state"
        className="w-[28px] h-[28px] rounded-[8px] inline-flex items-center justify-center hover:bg-black/[.06]" style={{ color: "#6C6C69" }}><MoreVertical size={15} /></button>
      {open ? (
        <div className="absolute right-0 top-[110%] z-30 rounded-[10px] p-1 w-[164px]" style={{ background: "#fff", border: "1px solid #ECECEF", boxShadow: "0 10px 28px rgba(20,20,18,.14)" }} data-testid={`state-menu-panel-${visitId}`}>
          {opts.map(([k, label, Icon]) => (
            <button key={k} onClick={() => { setOpen(false); if (cur !== k) onChange(visitId, k); }} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] text-[12.5px] hover:bg-black/[.04]" style={{ color: "#141412" }} data-testid={`state-opt-${k}-${visitId}`}>
              <Icon size={13} style={{ color: SESSION_STATES[k].color }} /> {label}
              {cur === k ? <Check size={12} className="ml-auto" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function StageDots({ st, compact }) {
  return (
    <div className="flex items-center gap-1" data-testid="pipeline-stages">
      {PIPELINE_STAGES.map((s, i) => {
        const done = st.done?.[s.key];
        const active = st.active === s.key;
        const err = st.failed && st.step === s.key;
        const bg = err ? "#FCE9E7" : done ? "#E7F7EC" : active ? "#EEF0FF" : "#F1F1F0";
        const fg = err ? "#B42318" : done ? "#166534" : active ? IND : "#9B9B97";
        const Icon = STAGE_ICON[s.key];
        return (
          <div key={s.key} className="flex items-center gap-1" title={s.label}>
            <span className="inline-flex items-center justify-center rounded-[6px]" style={{ width: compact ? 20 : 24, height: compact ? 20 : 24, background: bg, color: fg }} data-testid={`stage-${s.key}`}>
              {done ? <Check size={compact ? 11 : 13} /> : active ? <Loader2 size={compact ? 11 : 13} className="animate-spin" /> : err ? <AlertTriangle size={compact ? 11 : 13} /> : <Icon size={compact ? 11 : 13} />}
            </span>
            {i < PIPELINE_STAGES.length - 1 ? <span className="w-2 h-[2px] rounded-full" style={{ background: done ? "#8FCDA3" : "#E4E4E7" }} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function ClaimBadge({ st }) {
  if (st.complete) {
    const sent = (st.claim_status || "").toLowerCase();
    return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#E7F7EC", color: "#166534" }} data-testid="claim-badge">{sent === "sent" || sent === "queued" ? "Claim created" : `Claim ${st.claim_status || "ready"}`}</span>;
  }
  if (st.failed) return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FCE9E7", color: "#B42318" }} data-testid="claim-badge">Needs retry</span>;
  if (st.active) return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#EEF0FF", color: "#3730A3" }} data-testid="claim-badge">{st.label || "Processing…"}</span>;
  return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#F1F1F0", color: "#6C6C69" }} data-testid="claim-badge">Not started</span>;
}

// Real claim status from the note-to-claim note (not the visit's claim_status, which
// is a false indicator): Signed vs Not signed · Claim generated vs Not generated.
function ClaimStatus({ note }) {
  if (!note) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F1F1F0", color: "#9B9B97" }} data-testid="claim-status-loading">Checking status…</span>;
  const signed = isSigned(note);
  const generated = isGenerated(note);
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="claim-status">
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={signed ? { background: "#E7F7EC", color: "#166534" } : { background: "#FBF1E0", color: "#8A5A00" }} data-testid={`claim-signed-${signed ? "yes" : "no"}`}>{signed ? "Signed" : "Not signed"}</span>
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={generated ? { background: "#EDEBFF", color: "#3730A3" } : { background: "#F1F1F0", color: "#6C6C69" }} data-testid={`claim-generated-${generated ? "yes" : "no"}`}>{generated ? "Claim generated" : "Not generated"}</span>
    </span>
  );
}

function ActBtn({ onClick, busy, icon: Icon, children, testid, tone = "default" }) {
  const styles = tone === "primary" ? { background: "#141412", color: "#fff", border: "1px solid #141412" }
    : tone === "indigo" ? { background: "#4B4BE8", color: "#fff", border: "1px solid #4B4BE8" }
    : { background: "#fff", color: "#141412", border: "1px solid #E4E4E7" };
  return (
    <button onClick={onClick} disabled={busy} data-testid={testid} style={styles}
      className="text-[12px] font-bold px-3 py-1.5 rounded-[8px] inline-flex items-center gap-1.5 disabled:opacity-50 shrink-0">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {children}
    </button>
  );
}

// Contextual action for a completed visit based on its real claim status.
function RowClaimAction({ note, visitId, signingId, onSign, onOpenClaim }) {
  if (!note) return null;
  if (!isSigned(note)) return <ActBtn onClick={(e) => { e.stopPropagation(); onSign(note.id, visitId); }} busy={signingId === note.id} icon={PenLine} tone="primary" testid={`row-sign-${visitId}`}>Sign</ActBtn>;
  if (!isGenerated(note)) return <ActBtn onClick={(e) => { e.stopPropagation(); onOpenClaim?.(note.id); }} icon={FileCheck2} tone="indigo" testid={`row-generate-${visitId}`}>Generate claim</ActBtn>;
  return <ActBtn onClick={(e) => { e.stopPropagation(); onOpenClaim?.(note.id); }} icon={ExternalLink} testid={`row-openclaim-${visitId}`}>Open claim</ActBtn>;
}

export default function ActivityTracker({ appointments, clientId, onOpenClaim, onReviewing }) {
  const items = useMemo(
    () => (appointments || []).filter((a) => a.visit_id).sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)),
    [appointments],
  );
  const [visits, setVisits] = useState({});
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [batching, setBatching] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [collapsedSec, setCollapsedSec] = useState({ removed: true, stale: false });
  const [signingId, setSigningId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (vid) => setSelected((s) => { const n = new Set(s); n.has(vid) ? n.delete(vid) : n.add(vid); return n; });
  const clearSel = () => setSelected(new Set());
  const visitsRef = useRef({});
  useEffect(() => { visitsRef.current = visits; }, [visits]);
  useEffect(() => { onReviewing?.(!!openId); }, [openId, onReviewing]);

  const fetchOne = useCallback(async (vid) => {
    try {
      const v = await getVisit(vid);
      setVisits((m) => ({ ...m, [vid]: v }));
      // Pull the note-to-claim note for the REAL claim status (signed / generated).
      if (v?.note_id) getNote(v.note_id).then((nn) => { if (nn) setNotes((m) => ({ ...m, [v.note_id]: nn })); }).catch(() => {});
      return v;
    } catch { return null; }
  }, []);

  // Actively drive only visits that are still processing — NEVER a failed /
  // needs_manual recording (those go to the Failed list) and NEVER a frozen
  // (stale/removed) session (§6.10 — those stay out of all automation).
  const nudgeRef = useRef({});
  const driveIfNeeded = useCallback(async (v) => {
    if (!v || !v.visit_id) return;
    if (isFrozen(v)) return;
    const st = pipelineState(v);
    if (st.complete || st.failed || st.needsManual) return;
    const rec = nudgeRef.current[v.visit_id] || { last: 0, count: 0 };
    if (rec.count >= 2) return; // mirror platform's 2-attempt auto cap
    const now = Date.now();
    if (now - rec.last < 25000) return;
    nudgeRef.current[v.visit_id] = { last: now, count: rec.count + 1 };
    try { const nv = await transcribeVisit(v.visit_id); if (nv && nv.visit_id) setVisits((m) => ({ ...m, [nv.visit_id]: nv })); } catch { /* keep polling */ }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const fetched = await Promise.all(items.slice(0, 60).map((a) => fetchOne(a.visit_id)));
      if (alive) setLoading(false);
      fetched.forEach((v) => driveIfNeeded(v));
    })();
    return () => { alive = false; };
  }, [items, fetchOne, driveIfNeeded]);

  useEffect(() => {
    const iv = setInterval(async () => {
      // Pause polling while a note is open for review — no list churn, no auto-refresh.
      if (document.hidden || openId) return;
      for (const a of items) {
        const cur = visitsRef.current[a.visit_id];
        if (cur && isFrozen(cur)) continue;
        const st = pipelineState(cur);
        if (cur && !st.complete && !st.failed) { const fresh = await fetchOne(a.visit_id); driveIfNeeded(fresh || cur); }
      }
    }, 6000);
    return () => clearInterval(iv);
  }, [items, openId, fetchOne, driveIfNeeded]);

  const processAllPending = async () => {
    setBatching(true);
    try {
      const res = await processBatch({ all_pending: true, client_id: String(clientId || items[0]?.client_id || ""), full_pipeline: true });
      const n = res?.processed ?? res?.queued ?? res?.visit_ids?.length ?? 0;
      const skipped = res?.skipped_needs_manual?.length || 0;
      toast.success((n ? `Processing ${n} visit${n === 1 ? "" : "s"}` : "Bulk processing started") + (skipped ? ` · ${skipped} need manual retry` : ""));
      setTimeout(() => items.forEach((a) => fetchOne(a.visit_id)), 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start bulk processing"); }
    finally { setBatching(false); }
  };

  // Multi-select: run the same action across an array of visits (§ "run array of transactions").
  const changeState = async (vids, state) => {
    const list = Array.isArray(vids) ? vids : [vids];
    const results = await Promise.all(list.map((vid) => setSessionState(vid, state).then((v) => ({ vid, v })).catch(() => ({ vid, err: true }))));
    const ok = results.filter((r) => r.v);
    setVisits((m) => { const n = { ...m }; ok.forEach((r) => { n[r.vid] = (r.v && r.v.visit_id) ? r.v : { ...(n[r.vid] || {}), session_state: state }; }); return n; });
    clearSel();
    const fail = results.length - ok.length;
    const msg = `${ok.length} session${ok.length === 1 ? "" : "s"} ${state === "active" ? "reactivated" : "marked " + state}` + (fail ? ` · ${fail} failed` : "");
    if (ok.length) toast.success(msg); else toast.error(msg);
  };
  const processSelected = async () => {
    const ids = [...selected].filter((vid) => !isFrozen(visits[vid]));
    if (!ids.length) { toast.error("Select active sessions to process — frozen ones are skipped."); return; }
    setBatching(true);
    try {
      const res = await processBatch({ visit_ids: ids, client_id: String(clientId || items[0]?.client_id || ""), full_pipeline: true });
      const n = res?.processed ?? res?.queued ?? res?.visit_ids?.length ?? ids.length;
      const skipped = res?.skipped_needs_manual?.length || 0;
      toast.success((n ? `Processing ${n} visit${n === 1 ? "" : "s"}` : "Processing started") + (skipped ? ` · ${skipped} need manual retry` : ""));
      clearSel();
      setTimeout(() => ids.forEach((vid) => fetchOne(vid)), 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't process selected"); }
    finally { setBatching(false); }
  };

  // Sign a completed visit's note directly from the tracker (no need to open Create Claims).
  const signOne = async (noteId, vid) => {
    setSigningId(noteId);
    try {
      await signNote(noteId);
      const nn = await getNote(noteId);
      if (nn) setNotes((m) => ({ ...m, [noteId]: nn }));
      toast.success("Visit signed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't sign"); }
    finally { setSigningId(null); }
  };

  if (loading) return <div className="py-16 text-center text-[13px]" style={{ color: "#9B9B97" }} data-testid="tracker-loading"><Loader2 size={18} className="animate-spin mx-auto mb-2" /> Loading visit activity…</div>;
  if (!items.length) return (
    <div className="py-16 text-center" data-testid="tracker-empty">
      <Activity size={28} className="mx-auto mb-3" style={{ color: "#CFCFCB" }} />
      <div className="text-[14px] font-semibold" style={{ color: "#141412" }}>No completed visits yet</div>
      <div className="text-[12.5px] mt-1" style={{ color: "#9B9B97" }}>Once a telehealth visit ends, its recording is transcribed into a SOAP note and claim automatically — it will appear here.</div>
    </div>
  );

  const live = [], failed = [], stale = [], removed = [];
  for (const a of items) {
    const v = visits[a.visit_id];
    const st = pipelineState(v);
    const ss = v?.session_state;
    const entry = { a, st };
    if (ss === "stale") stale.push(entry);
    else if (ss === "removed") removed.push(entry);
    else if (st.failed || st.needsManual) failed.push(entry);
    else live.push(entry);
  }
  const pendingCount = live.filter(({ st }) => !st.complete).length;

  // Backlog filter by real claim status (completed visits only).
  const noteOf = (vid) => { const v = visits[vid]; return v?.note_id ? notes[v.note_id] : null; };
  const liveShown = statusFilter === "all" ? live : live.filter(({ a, st }) => {
    if (!st.complete) return false;
    const n = noteOf(a.visit_id);
    if (!n) return false;
    if (statusFilter === "unsigned") return !isSigned(n);
    if (statusFilter === "signed") return isSigned(n) && !isGenerated(n);
    if (statusFilter === "generated") return isGenerated(n);
    return true;
  });
  const countBy = (f) => live.filter(({ a, st }) => { const n = noteOf(a.visit_id); return st.complete && n && f(n); }).length;
  const FILTERS = [
    ["all", "All", live.length],
    ["unsigned", "Unsigned", countBy((n) => !isSigned(n))],
    ["signed", "Signed", countBy((n) => isSigned(n) && !isGenerated(n))],
    ["generated", "Generated", countBy((n) => isGenerated(n))],
  ];

  const openVisit = openId ? visits[openId] : null;
  const openAppt = openId ? items.find((a) => a.visit_id === openId) : null;

  const Row = ({ a, st, failedRow, onRetry, retrying }) => {
    const v = visits[a.visit_id];
    const ss = v?.session_state || "active";
    const rowNote = v?.note_id ? notes[v.note_id] : null;
    const checked = selected.has(a.visit_id);
    return (
      <div data-testid={`tracker-row-${a.visit_id}`} onClick={() => setOpenId(a.visit_id)}
        className="w-full text-left rounded-[12px] p-3 flex items-center gap-3 transition-colors hover:bg-black/[.015] cursor-pointer" style={{ background: "#fff", border: checked ? "1px solid #4B4BE8" : "1px solid #ECECEF" }}>
        <button onClick={(e) => { e.stopPropagation(); toggleSel(a.visit_id); }} data-testid={`tracker-select-${a.visit_id}`} title="Select" className="shrink-0" style={{ color: checked ? "#4B4BE8" : "#B4B4B1" }}>
          {checked ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-bold" style={{ color: "#141412" }}>{a.patient?.name || "Patient"}</span>
            {st.complete ? <ClaimStatus note={rowNote} /> : <ClaimBadge st={st} />}
            <SessionBadge state={ss} />
            <VisitIdChip visitId={a.visit_id} />
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8A8A86" }}>{fmtDT(a.scheduled_at)} · {a.provider_name || "—"}{failedRow && st.message ? ` · ${st.message}` : ""}</div>
        </div>
        {failedRow ? (
          <button onClick={(e) => { e.stopPropagation(); onRetry(a.visit_id); }} disabled={retrying} data-testid={`tracker-retry-${a.visit_id}`}
            className="text-[12px] font-bold px-3 py-1.5 rounded-[8px] inline-flex items-center gap-1.5 disabled:opacity-50" style={{ border: "1px solid #E4E4E7", color: "#141412" }}>
            {retrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Retry
          </button>
        ) : st.complete ? <RowClaimAction note={rowNote} visitId={a.visit_id} signingId={signingId} onSign={signOne} onOpenClaim={onOpenClaim} /> : <StageDots st={st} compact />}
        <StateControl visitId={a.visit_id} state={ss} onChange={changeState} />
      </div>
    );
  };

  const FrozenSection = ({ title, color, icon: Icon, rows, testid, secKey }) => {
    if (!rows.length) return null;
    const open = !collapsedSec[secKey];
    const Chevron = open ? ChevronDown : ChevronRight;
    return (
      <div className="mt-6" data-testid={testid}>
        <button onClick={() => setCollapsedSec((c) => ({ ...c, [secKey]: open }))} data-testid={`${testid}-toggle`} className="w-full flex items-center gap-1.5 mb-2 px-1 text-left">
          <Chevron size={14} style={{ color }} />
          <Icon size={14} style={{ color }} />
          <span className="text-[12px] font-bold uppercase tracking-[.04em]" style={{ color }}>{title} ({rows.length})</span>
          <span className="text-[11px]" style={{ color: "#9B9B97" }}>· frozen — excluded from all automatic processing</span>
        </button>
        {open ? <div className="space-y-2">{rows.map(({ a, st }) => <Row key={a.visit_id} a={a} st={st} />)}</div> : null}
      </div>
    );
  };

  return (
    <div data-testid="activity-tracker">
      {selected.size ? (
        <div className="flex items-center gap-2 flex-wrap mb-3 px-3 py-2 rounded-[10px] sticky top-0 z-20" style={{ background: "#141412", color: "#fff" }} data-testid="tracker-selbar">
          <span className="text-[12px] font-bold">{selected.size} selected</span>
          <span className="flex-1" />
          <SelBtn onClick={processSelected} disabled={batching} icon={batching ? Loader2 : Zap} spin={batching} testid="selbar-process">Process</SelBtn>
          <SelBtn onClick={() => changeState([...selected], "stale")} icon={Pause} testid="selbar-stale">Mark stale</SelBtn>
          <SelBtn onClick={() => changeState([...selected], "removed")} icon={Archive} testid="selbar-removed">Mark removed</SelBtn>
          <SelBtn onClick={() => changeState([...selected], "active")} icon={PlayCircle} testid="selbar-active">Mark active</SelBtn>
          <button onClick={clearSel} data-testid="selbar-clear" className="text-[12px] font-semibold px-2 py-1 rounded-[7px] hover:bg-white/10">Clear</button>
        </div>
      ) : null}

      {pendingCount > 0 ? (
        <div className="flex items-center gap-2 mb-3 px-1">
          <span className="text-[12px]" style={{ color: "#6C6C69" }}>{pendingCount} visit{pendingCount === 1 ? "" : "s"} still processing</span>
          <span className="flex-1" />
          <button onClick={processAllPending} disabled={batching} data-testid="tracker-process-all" className="text-[12px] font-bold px-3 py-1.5 rounded-[8px] inline-flex items-center gap-1.5 text-white disabled:opacity-50" style={{ background: "#141412" }}>
            {batching ? <Loader2 size={12} className="animate-spin" /> : <Zap size={13} />} Process all pending
          </button>
        </div>
      ) : null}

      <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="tracker-status-filter">
        {FILTERS.map(([k, label, n]) => {
          const on = statusFilter === k;
          return (
            <button key={k} onClick={() => setStatusFilter(k)} data-testid={`status-filter-${k}`}
              className="text-[12px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-colors"
              style={on ? { background: "#141412", color: "#fff" } : { background: "#F1F1F0", color: "#6C6C69" }}>
              {label}<span className="text-[11px] font-bold" style={{ opacity: .7 }}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {liveShown.length ? liveShown.map(({ a, st }) => <Row key={a.visit_id} a={a} st={st} />)
          : <div className="text-[12.5px] py-4 text-center" style={{ color: "#9B9B97" }} data-testid="tracker-live-empty">{statusFilter === "all" ? "No active visits in progress." : `No ${statusFilter} visits.`}</div>}
      </div>

      {failed.length ? (
        <div className="mt-6" data-testid="failed-recordings">
          <div className="flex items-center gap-1.5 mb-2 px-1">
            <FileWarning size={14} style={{ color: "#B42318" }} />
            <span className="text-[12px] font-bold uppercase tracking-[.04em]" style={{ color: "#B42318" }}>Failed recordings ({failed.length})</span>
          </div>
          <div className="space-y-2">
            {failed.map(({ a, st }) => <FailedRow key={a.visit_id} a={a} st={st} setOpenId={setOpenId} onDone={(v) => setVisits((m) => ({ ...m, [v.visit_id]: v }))} Row={Row} />)}
          </div>
        </div>
      ) : null}

      <FrozenSection title="Stale" color="#9A5A00" icon={Pause} rows={stale} testid="stale-sessions" secKey="stale" />
      <FrozenSection title="Removed" color="#B42318" icon={Archive} rows={removed} testid="removed-sessions" secKey="removed" />

      {openVisit ? (
        <VisitDetail visit={openVisit} appt={openAppt} note={openVisit.note_id ? notes[openVisit.note_id] : null} onClose={() => setOpenId(null)} onRefresh={() => fetchOne(openId)}
          onLocalUpdate={(patch) => setVisits((m) => ({ ...m, [openId]: { ...m[openId], ...patch } }))} onOpenClaim={onOpenClaim} onChangeState={changeState} />
      ) : null}
    </div>
  );
}

function FailedRow({ a, st, onDone, Row }) {
  const [retrying, setRetrying] = useState(false);
  const retry = async (vid) => {
    setRetrying(true);
    try { const v = await transcribeVisit(vid); if (v && v.visit_id) onDone(v); toast.success("Retry started"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(false); }
  };
  return <Row a={a} st={st} failedRow onRetry={retry} retrying={retrying} />;
}

/* ---------------- Detail drawer ---------------- */
function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-[12px] p-3.5 mb-3" style={{ background: "#fff", border: "1px solid #ECECEF" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2"><Icon size={15} style={{ color: IND }} /><span className="text-[12px] font-bold uppercase tracking-[.04em]" style={{ color: "#141412" }}>{title}</span></div>
      {children}
    </div>
  );
}

const capFirst = (s) => (typeof s === "string" && s) ? s.charAt(0).toUpperCase() + s.slice(1) : s;
const itemText = (x) => typeof x === "string" ? x : (x?.text || x?.label || x?.name || x?.description || x?.flag || x?.symptom || JSON.stringify(x));

function InsBlock({ label, warn, children }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-[.05em] mb-0.5" style={{ color: warn ? "#9A5A00" : "#8A8A86" }}>{label}</div>
      {children}
    </div>
  );
}
function InsList({ label, items, warn }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <InsBlock label={label} warn={warn}>
      <ul className="list-disc pl-4 text-[12.5px] space-y-0.5" style={{ color: warn ? "#9A5A00" : "#3F4551" }}>
        {items.map((x, i) => <li key={i}>{itemText(x)}</li>)}
      </ul>
    </InsBlock>
  );
}
function Mood({ mood }) {
  if (!mood || typeof mood !== "object") return null;
  return (
    <div className="text-[12.5px]" style={{ color: "#3F4551" }}>
      <span className="font-semibold">{capFirst(mood.primary) || "—"}</span>
      {mood.secondary ? <span>, {mood.secondary}</span> : null}
      {typeof mood.confidence === "number" ? <span style={{ color: "#9B9B97" }}> · {Math.round(mood.confidence * 100)}% confidence</span> : null}
      {mood.notes ? <div className="mt-0.5" style={{ color: "#6C6C69" }}>{mood.notes}</div> : null}
    </div>
  );
}
function Responsiveness({ resp }) {
  if (!resp || typeof resp !== "object") return null;
  const rows = Object.entries(resp).filter(([, v]) => v && typeof v === "object" && ("score" in v || "label" in v));
  if (!rows.length) return null;
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-[12.5px]">
          <span className="font-semibold capitalize shrink-0" style={{ color: "#141412", minWidth: 118 }}>{k.replace(/_/g, " ")}</span>
          {typeof v.score === "number" ? <span className="tabular-nums font-bold shrink-0" style={{ color: IND }}>{v.score}/10</span> : null}
          {v.label ? <span style={{ color: "#6C6C69" }}>{v.label}</span> : null}
        </div>
      ))}
    </div>
  );
}

function Insights({ data }) {
  if (!data || typeof data !== "object") return <div className="text-[12px]" style={{ color: "#9B9B97" }}>No insights yet.</div>;
  const symptoms = data.key_symptoms || data.symptoms;
  const observations = data.provider_observations || data.observations;
  const problems = data.problems_observations;
  const flags = data.clinical_flags || data.flags;
  const has = data.summary || symptoms?.length || observations?.length || problems?.length || flags?.length || data.mood || data.responsiveness;
  if (!has) return <div className="text-[12px]" style={{ color: "#9B9B97" }}>No insights yet.</div>;
  return (
    <div className="space-y-2.5">
      {data.summary ? <InsBlock label="Summary"><div className="text-[12.5px] leading-relaxed" style={{ color: "#3F4551" }}>{data.summary}</div></InsBlock> : null}
      <InsList label="Key symptoms" items={symptoms} />
      <InsList label="Provider observations" items={observations} />
      <InsList label="Problems observed" items={problems} />
      {data.mood ? <InsBlock label="Mood"><Mood mood={data.mood} /></InsBlock> : null}
      {data.responsiveness ? <InsBlock label="Responsiveness"><Responsiveness resp={data.responsiveness} /></InsBlock> : null}
      <InsList label="Clinical flags" items={flags} warn />
    </div>
  );
}

function Transcript({ data }) {
  if (!data) return <div className="text-[12px]" style={{ color: "#9B9B97" }}>No transcript yet.</div>;
  if (Array.isArray(data)) return <div className="space-y-1.5 max-h-[260px] overflow-y-auto">{data.map((t, i) => <div key={i} className="text-[12.5px]" style={{ color: "#3F4551" }}>{t.speaker ? <span className="font-bold" style={{ color: "#141412" }}>{t.speaker}: </span> : null}{t.text || t.content || ""}</div>)}</div>;
  return <div className="text-[12.5px] whitespace-pre-wrap max-h-[260px] overflow-y-auto" style={{ color: "#3F4551" }}>{String(data)}</div>;
}

function Documents({ apptId }) {
  const [docs, setDocs] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  useEffect(() => { let alive = true; getAppointmentDocuments(apptId).then((d) => { if (alive) setDocs(d || []); }).catch(() => { if (alive) setDocs([]); }); return () => { alive = false; }; }, [apptId]);
  if (docs === null) return <div className="text-[12px]" style={{ color: "#9B9B97" }}><Loader2 size={13} className="animate-spin inline mr-1" /> Loading…</div>;
  if (!docs.length) return <div className="text-[12px]" style={{ color: "#9B9B97" }}>No documents uploaded for this visit.</div>;
  return (
    <div className="space-y-1.5" data-testid="visit-documents">
      {docs.map((d, i) => {
        const id = d.id || d.doc_id || d.document_id;
        const name = d.name || d.filename || d.title || `Document ${i + 1}`;
        const isOpen = openDoc && (openDoc.id || openDoc.doc_id || openDoc.document_id) === id;
        return (
          <div key={id || i} className="rounded-[9px] px-3 py-2" style={{ background: "#FBFBFA", border: "1px solid #ECECEF" }} data-testid={`visit-doc-${i}`}>
            <button onClick={async () => { if (isOpen) { setOpenDoc(null); return; } try { const full = await getAppointmentDocument(apptId, id); setOpenDoc(full || d); } catch { setOpenDoc(d); } }}
              className="w-full flex items-center gap-2 text-left">
              <FileText size={14} style={{ color: IND }} />
              <span className="text-[12.5px] font-semibold flex-1 min-w-0 truncate" style={{ color: "#141412" }}>{name}</span>
              <span className="text-[11px]" style={{ color: IND }}>{isOpen ? "Hide" : "View"}</span>
            </button>
            {isOpen ? <div className="mt-2 text-[12px] whitespace-pre-wrap max-h-[220px] overflow-y-auto" style={{ color: "#3F4551" }}>{openDoc.extraction || openDoc.text || openDoc.content || openDoc.extracted_text || "No extracted text."}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

function VisitDetail({ visit, appt, note, onClose, onRefresh, onLocalUpdate, onOpenClaim, onChangeState }) {
  const st = pipelineState(visit);
  const frozen = isFrozen(visit);
  const ss = visit.session_state || "active";
  const [busy, setBusy] = useState(null);
  const { openNote } = useChartTabs();
  const recordingUrl = visit.recording_url || visit.recording_download_url || visit.media_url || null;
  const displaySoap = (note && (note.encounter?.formatted_note || note.soap_text)) || visit.soap_text || "";

  const run = async (label, fn) => {
    setBusy(label);
    try { const v = await fn(); if (v && v.visit_id) onLocalUpdate(v); toast.success(label); setTimeout(onRefresh, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); } finally { setBusy(null); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 4450, background: "rgba(20,20,18,.32)", display: "flex", justifyContent: "flex-end" }} onMouseDown={onClose}>
      <aside onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(560px,97vw)", height: "100%", background: "#FBFBFA", display: "flex", flexDirection: "column" }} data-testid="visit-detail">
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #ECECEF", background: "#fff" }}>
          <Activity size={16} style={{ color: IND }} />
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-bold" style={{ color: "#141412" }}>{appt?.patient?.name || visit.patient?.name || "Visit"}</div>
            <div className="text-[11.5px] truncate flex items-center gap-1.5" style={{ color: "#8A8A86" }}>{fmtDT(appt?.scheduled_at)} · {visit.provider_name || "—"}</div>
          </div>
          <VisitIdChip visitId={visit.visit_id} />
          <SessionBadge state={ss} />
          <StateControl visitId={visit.visit_id} state={ss} onChange={onChangeState} />
          <button onClick={onClose} data-testid="visit-detail-close" className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={16} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="rounded-[12px] p-3.5 mb-3" style={{ background: "#fff", border: "1px solid #ECECEF" }} data-testid="visit-pipeline">
            <div className="flex items-center justify-between mb-2.5"><span className="text-[12px] font-bold uppercase tracking-[.04em]" style={{ color: "#141412" }}>Pipeline</span>{st.complete ? <ClaimStatus note={note} /> : <ClaimBadge st={st} />}</div>
            <div className="flex items-center justify-between">
              {PIPELINE_STAGES.map((s) => {
                // "Claim" is green ONLY when the claim is actually generated (note.claim_status
                // === "queued") — not merely when a note exists (the upstream false indicator).
                const done = s.key === "claim" ? isGenerated(note) : st.done?.[s.key];
                const active = st.active === s.key; const err = st.failed && st.step === s.key;
                return <div key={s.key} className="flex flex-col items-center gap-1 flex-1">
                  <StageOne done={done} active={active} stalled={err} k={s.key} />
                  <span className="text-[10px] font-semibold" style={{ color: done ? "#166534" : active ? IND : err ? "#B42318" : "#9B9B97" }}>{s.label}</span>
                </div>;
              })}
            </div>
            {st.error ? <div className="text-[11.5px] mt-2.5 px-2.5 py-1.5 rounded-[8px]" style={st.failed ? { background: "#FCE9E7", color: "#B42318" } : { background: "#FFF4E0", color: "#9A5A00" }} data-testid="visit-error">{st.message || st.error}</div> : null}
          </div>

          <div className="flex flex-wrap gap-2 mb-3" data-testid="visit-controls">
            {frozen ? (
              <div className="w-full flex items-center gap-2 px-3 py-2 rounded-[9px]" style={{ background: SESSION_STATES[ss].bg, color: SESSION_STATES[ss].color }} data-testid="visit-frozen-notice">
                <AlertTriangle size={14} />
                <span className="text-[12px] font-semibold flex-1">Session is {SESSION_STATES[ss].label.toLowerCase()} — no fetch, transcribe or pipeline will run until it's active.</span>
                <button onClick={() => onChangeState?.(visit.visit_id, "active")} data-testid="visit-mark-active" className="text-[12px] font-bold px-3 py-1.5 rounded-[8px] inline-flex items-center gap-1.5" style={{ background: "#141412", color: "#fff" }}><PlayCircle size={13} /> Mark active</button>
              </div>
            ) : (<>
              {st.complete && note && !isSigned(note) ? <Ctl onClick={() => run("Visit signed", () => signNote(note.id))} busy={busy} label="Visit signed" icon={PenLine} primary testid="ctl-sign">Sign visit</Ctl> : null}
              {st.complete && note && isSigned(note) && !isGenerated(note) ? <Ctl onClick={() => onOpenClaim?.(note.id)} busy={busy} label="" icon={FileCheck2} primary testid="ctl-generate">Generate claim</Ctl> : null}
              <Ctl onClick={() => run("Transcription started", () => transcribeVisit(visit.visit_id))} busy={busy} label="Transcription started" icon={Mic} testid="ctl-transcribe">Transcribe only</Ctl>
              {st.done?.soap && !st.complete ? <Ctl onClick={() => run("Filed for coding", () => sendVisitToClaim(visit.visit_id))} busy={busy} label="Filed for coding" icon={Send} testid="ctl-send-claim">Save encounter</Ctl> : null}
              {st.note_id ? <Ctl onClick={() => onOpenClaim?.(st.note_id)} busy={busy} label="" icon={ExternalLink} testid="ctl-open-claim">Open claim</Ctl> : null}
            </>)}
          </div>

          <Section icon={FileText} title="Pre-visit documents" testid="visit-documents-section"><Documents apptId={visit.appointment_id || appt?.appointment_id} /></Section>
          <Section icon={Video} title="Recording" testid="visit-recording">
            {recordingUrl ? <video src={recordingUrl} controls className="w-full rounded-[8px]" style={{ maxHeight: 220, background: "#000" }} data-testid="visit-recording-player" />
              : <div className="text-[12px]" style={{ color: "#9B9B97" }}>{st.failed ? (st.message || "No usable recording.") : visit.recording_id ? "Recording captured — processed securely server-side (no direct playback link)." : "No recording yet."}</div>}
          </Section>
          <Section icon={Sparkles} title="Key insights" testid="visit-insights"><Insights data={visit.insights} /></Section>
          <Section icon={Stethoscope} title="SOAP note" testid="visit-soap">
            {displaySoap ? <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: "#3F4551" }} data-testid="visit-soap-text">{displaySoap}</div> : <div className="text-[12px]" style={{ color: "#9B9B97" }}>No SOAP note yet.</div>}
            {visit.note_id ? <button onClick={() => openNote?.({ noteId: visit.note_id, name: patientOf(note) || "Patient", chartUuid: note?.chart_uuid || visit.patient_chart_uuid || null, fromLabel: "Telehealth" })} data-testid="visit-soap-edit" className="mt-2 text-[12px] font-bold" style={{ color: IND }}>Edit SOAP</button>
              : displaySoap ? <div className="mt-2 text-[11px]" style={{ color: "#9B9B97" }}>Run the pipeline to create the claim note before editing.</div> : null}
          </Section>
          <Section icon={Mic} title="Transcript" testid="visit-transcript"><Transcript data={visit.transcript} /></Section>
        </div>
      </aside>
    </div>
  );
}

function SelBtn({ onClick, disabled, icon: Icon, spin, children, testid }) {
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className="text-[12px] font-bold px-2.5 py-1 rounded-[7px] inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "rgba(255,255,255,.14)", color: "#fff" }}><Icon size={12} className={spin ? "animate-spin" : ""} /> {children}</button>;
}

function StageOne({ done, active, stalled, k }) {
  const Icon = STAGE_ICON[k];
  const bg = stalled ? "#FCE9E7" : done ? "#E7F7EC" : active ? "#EEF0FF" : "#F1F1F0";
  const fg = stalled ? "#B42318" : done ? "#166534" : active ? IND : "#9B9B97";
  return <span className="inline-flex items-center justify-center rounded-full" style={{ width: 30, height: 30, background: bg, color: fg }}>{done ? <Check size={15} /> : active ? <Loader2 size={15} className="animate-spin" /> : stalled ? <AlertTriangle size={15} /> : <Icon size={14} />}</span>;
}

function Ctl({ onClick, busy, label, icon: Icon, primary, children, testid }) {
  const isBusy = busy && busy === label;
  return (
    <button onClick={onClick} disabled={!!busy} data-testid={testid}
      className="h-9 px-3.5 rounded-[9px] text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
      style={primary ? { background: "#141412", color: "#fff" } : { border: "1px solid #E4E4E7", color: "#141412", background: "#fff" }}>
      {isBusy ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {children}
    </button>
  );
}
